import * as React from "react";
import { clsx } from "@plans/appkit";
import { AlertTriangleIcon } from "@plans/icons/untitled-ui/AlertTriangleIcon";
import * as Sentry from "@sentry/react-router";
import { StatusCodes } from "http-status-codes";
import { useTranslation } from "react-i18next";
import { isRouteErrorResponse, useRouteError } from "react-router";
import { isErrorResponseBody } from "~/api";
import { useDialog } from "~/lib/ui/DialogContext";
import {
	isClientRouteErrorResponse,
	isSingleFetchNoResultError,
} from "~/monitoring/sentry-filters";
import { Button } from "../atoms/Button";
import { ErrorDisplay } from "../atoms/ErrorDisplay";

export const SUPPORT_EMAIL = "support@plans.app";

type GeneralErrorBoundaryProps = {
	size?: "small" | "large";
	children?: React.ReactNode;
};

export function GeneralErrorBoundary(props: GeneralErrorBoundaryProps) {
	const { t } = useTranslation(["common"]);
	const error = useRouteError();

	React.useEffect(() => {
		// `@sentry/react-router` doesn't ship a dedicated boundary helper —
		// the framework integration captures route errors via `sentryOnError`
		// in `entry.client.tsx` and `handleError` in `entry.server.tsx`. We
		// still call `captureException` here for
		// belt-and-braces in case the boundary swallows something the
		// framework hooks didn't see.
		//
		// Short-circuit on noise that `monitoring.client.tsx`'s `beforeSend`
		// would also drop, to avoid serializing the error needlessly:
		// - `SingleFetchNoResultError`: transient RR race (see sentry-filters).
		// - 4xx route error responses: intentional control flow (auth
		//   redirects, 403, 404). React Router rethrows them as opaque
		//   `ErrorResponseImpl` objects that Sentry can only describe as
		//   "Object captured as exception with keys: data, internal, status,
		//   statusText" — see WEB-MAIN-1BV / 1BW.
		if (
			isSingleFetchNoResultError(error) ||
			isClientRouteErrorResponse(error)
		) {
			return;
		}
		Sentry.captureException(error);
	}, [error]);

	return (
		<div
			className={clsx(
				"flex items-center justify-center text-center",
				props.size === "large" ? "w-full" : "py-20",
			)}
			data-cy="general-error-boundary"
		>
			<div
				className={clsx(
					"outline-background-medium flex flex-col items-center justify-center gap-8 rounded-lg px-7 py-5 shadow-md outline-1",
					props.size === "large" && "min-h-[calc(100svh-_200px)] w-full",
				)}
			>
				{props.size === "large" && (
					<AlertTriangleIcon className="mx-auto h-12 w-12" />
				)}
				<GeneralErrorBoundaryInner error={error} />
				{props.children}
				<p className="text-text-secondary text-sm">
					{t("reach_out_if_persist")}{" "}
					<a className="text-accent-info" href={`mailto:${SUPPORT_EMAIL}`}>
						{SUPPORT_EMAIL}
					</a>
					{"."}
				</p>
			</div>
		</div>
	);
}

function LocalStorageExceededError() {
	const { confirm } = useDialog();
	const { t } = useTranslation(["common"]);

	return (
		<ErrorDisplay
			status={StatusCodes.INSUFFICIENT_SPACE_ON_RESOURCE}
			title={t("common:localstorage_exceeded_title")}
			message={t("common:localstorage_exceeded_message")}
			button={
				<Button
					onClick={async () => {
						const didConfirm = await confirm({
							heading: t("common:localstorage_exceeded_confirm"),
						});

						if (didConfirm) {
							localStorage.clear();
							window.location.reload();
						}
					}}
					variant="primary"
				>
					{t("common:localstorage_exceeded_action")}
				</Button>
			}
		/>
	);
}

export function GeneralErrorBoundaryInner({ error }: { error: unknown }) {
	const { t } = useTranslation(["common"]);

	if (!isRouteErrorResponse(error)) {
		if (error instanceof DOMException && error.name === "QuotaExceededError") {
			return <LocalStorageExceededError />;
		}
		return "An unexpected error occurred";
	}

	switch (error.status) {
		case StatusCodes.NOT_FOUND: {
			return (
				<ErrorDisplay
					status={StatusCodes.NOT_FOUND}
					title={t("common:404_title")}
					button={
						<Button href="/" variant="primary">
							{t("common:go_to_home")}
						</Button>
					}
				/>
			);
		}
		case StatusCodes.FORBIDDEN: {
			return (
				<ErrorDisplay
					status={StatusCodes.FORBIDDEN}
					title={t("common:403_title")}
					message={t("common:403_message")}
					button={
						<Button href="/" variant="primary">
							{t("common:go_to_login")}
						</Button>
					}
				/>
			);
		}
		default: {
			return (
				<ErrorDisplay
					status={error.status}
					message={
						isErrorResponseBody(error.data)
							? error.data.error.message
							: JSON.stringify(error.data)
					}
				/>
			);
		}
	}
}
